import Alpine from './alpine'

/**
 * The line builder shared by purchase orders and transfers.
 *
 * Both answer the same question - which variants, and how many - so they use one
 * component rather than two that drift apart. It keeps a blank row at the end so
 * adding the next line is not a click on "add" first; the server drops the blanks.
 */
Alpine.data('stockLines', (config = {}) => ({
    lines: config.lines ?? [],
    searchUrl: config.searchUrl ?? '',
    withCost: config.withCost ?? false,

    picker: { open: false, query: '', results: [], loading: false, timer: null },

    init() {
        this.ensureBlank()
    },

    openPicker() {
        this.picker.open = true
        this.picker.query = ''
        this.search()
    },

    search() {
        window.clearTimeout(this.picker.timer)

        // Debounced: a picker that fired on every keystroke would send a request
        // per letter and answer them out of order.
        this.picker.timer = window.setTimeout(async () => {
            this.picker.loading = true

            try {
                const response = await fetch(`${this.searchUrl}?q=${encodeURIComponent(this.picker.query)}`, {
                    headers: { Accept: 'application/json' },
                })

                this.picker.results = response.ok ? await response.json() : []
            } catch (e) {
                this.picker.results = []
            } finally {
                this.picker.loading = false
            }
        }, 250)
    },

    add(variant) {
        const existing = this.lines.find((line) => Number(line.variant_id) === Number(variant.variant_id))

        // Choosing the same variant twice means "more of it", not a duplicate row
        // the server would have to reconcile.
        if (existing) {
            existing.quantity = Number(existing.quantity || 0) + 1
        } else {
            this.lines.splice(this.lines.length - 1, 0, {
                variant_id: variant.variant_id,
                title: variant.title + (variant.variant_title ? ` - ${variant.variant_title}` : ''),
                sku: variant.sku,
                quantity: 1,
                cost: this.withCost ? (variant.cost ?? '') : null,
            })
        }

        this.picker.open = false
        this.ensureBlank()
    },

    remove(index) {
        this.lines.splice(index, 1)
        this.ensureBlank()
    },

    ensureBlank() {
        const last = this.lines[this.lines.length - 1]

        if (! last || last.variant_id) {
            this.lines.push({ variant_id: null, title: '', sku: '', quantity: 0, cost: null })
        }
    },

    get totalUnits() {
        return this.lines.reduce((total, line) => total + Number(line.quantity || 0), 0)
    },

    get subtotal() {
        return this.lines.reduce(
            (total, line) => total + Number(line.quantity || 0) * Number(line.cost || 0),
            0,
        )
    },
}))
